import { useState } from 'react'
import { Notice } from '../Toast'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createStaff, getInstitutionSecurity, getStaff, setInstitutionSecurity } from '../api/endpoints'
import { ErrorNotice } from '../components'
import { ApiError } from '../api/client'
import HowItWorks from '../HowItWorks'
import { ROLE_LABELS } from '../lib/format'
import { STAFF_ROLES } from '../auth/roles'

export default function StaffPage() {
  const queryClient = useQueryClient()
  const staff = useQuery({ queryKey: ['staff'], queryFn: getStaff })
  const [form, setForm] = useState({ email: '', name: '', password: '', role: 'ADMISSIONS_ADMIN' })
  const [created, setCreated] = useState<string | null>(null)
  const create = useMutation({
    mutationFn: () => createStaff({ email: form.email, name: form.name, password: form.password, roles: [form.role] }),
    onSuccess: async () => { setCreated(form.email); setForm({ email: '', name: '', password: '', role: form.role }); await queryClient.invalidateQueries({ queryKey: ['staff'] }) },
  })
  return (
    <div>
      <h1>Staff</h1>
      <HowItWorks id="staff" title="How staff access works" steps={[{ icon: 'staff', title: "Add a person", text: "Pick a role. Tell them their first password in person, not by email." },{ icon: 'shield', title: "They sign in", text: "With a password and an authenticator app, or with Microsoft if you allow it." },{ icon: 'edit', title: "You stay in control", text: "Choose below how staff sign in. Every change is audit-logged." }]} />
      <SignInSettings />
      <ErrorNotice error={staff.error} />
      <div className="table-wrap"><table>
        <thead><tr><th>Name</th><th>Email</th><th>Roles</th><th>Two-step verification</th></tr></thead>
        <tbody>{staff.data?.map((s) => <tr key={s.id}><td>{s.name}</td><td>{s.email}</td><td>{s.roles.map((r) => ROLE_LABELS[r] ?? r).join(', ')}</td><td>{s.mfaEnrolled ? <span className="badge ok">On</span> : <span className="badge warn">Not set up yet</span>}</td></tr>)}</tbody>
      </table></div>
      <form className="card" style={{ marginTop: 16, maxWidth: 480 }} onSubmit={(e) => { e.preventDefault(); setCreated(null); create.mutate() }}>
        <h2>Add a staff member</h2>
        <p className="muted">Tell them their initial password in person or by phone, not by email. They set up two-step verification at first sign-in.</p>
        <label className="field">Name<input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></label>
        <label className="field">Email<input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></label>
        <label className="field">Initial password (12+ characters)<input type="password" minLength={12} required autoComplete="new-password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} /></label>
        <label className="field">Role<select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}>{STAFF_ROLES.map((r) => <option key={r} value={r}>{ROLE_LABELS[r]}</option>)}</select></label>
        <ErrorNotice error={create.error} />
        {created && <Notice tone="ok">{created} was added.</Notice>}
        <button className="primary" type="submit" disabled={create.isPending}>{create.isPending ? 'Adding…' : 'Add staff member'}</button>
      </form>
    </div>
  )
}

/** How staff sign in. Two-step verification is on unless a super admin turns it off here; Microsoft sign-in is off until turned on. */
function SignInSettings() {
  const queryClient = useQueryClient()
  const current = useQuery({ queryKey: ['institution-security'], queryFn: getInstitutionSecurity })
  const save = useMutation({ mutationFn: setInstitutionSecurity, onSuccess: (d) => queryClient.setQueryData(['institution-security'], d) })
  const d = current.data
  return (
    <section className="card" aria-labelledby="signin-title" style={{ marginBottom: 16, maxWidth: 720 }}>
      <h2 id="signin-title">How staff sign in</h2>
      <ErrorNotice error={current.error ?? save.error} />
      {d && (
        <>
          <label className="row" style={{ marginBottom: 8 }}>
            <input type="checkbox" checked={d.requireMfa} disabled={save.isPending} onChange={(e) => save.mutate({ requireMfa: e.target.checked, microsoftSso: d.microsoftSso })} />
            <b>Require two-step verification for password sign-in</b>
          </label>
          <p className="muted">Recommended. Turned off, an admin can sign in with just a password. Anyone who has already set up an authenticator app is still asked for a code.</p>
          <label className="row" style={{ marginBottom: 8 }}>
            <input type="checkbox" checked={d.microsoftSso} disabled={save.isPending || !d.microsoftConfigured} onChange={(e) => save.mutate({ requireMfa: d.requireMfa, microsoftSso: e.target.checked })} />
            <b>Allow “Sign in with Microsoft”</b>
          </label>
          {d.microsoftConfigured && d.microsoftMultiTenant && (
            <TenantField current={d.microsoftTenantId ?? ''} busy={save.isPending} error={save.error} onSave={(t) => save.mutate({ requireMfa: d.requireMfa, microsoftSso: d.microsoftSso, microsoftTenantId: t })} />
          )}
          <p className="muted">{d.microsoftConfigured ? 'Only people who already have an account here, with the same email, can use it. Microsoft applies your organisation’s own verification, so no extra code is asked.' : 'Not available yet: the server has no Microsoft app registration configured.'}</p>
          {!d.requireMfa && !d.microsoftSso && <Notice tone="error">Two-step verification is off and Microsoft sign-in is off, so a password is the only protection on admin accounts.</Notice>}
        </>
      )}
    </section>
  )
}

/** Multi tenant sign-in: the institution says which Microsoft organisation its staff belong to, and only that one is let in. */
function TenantField({ current, busy, error, onSave }: { current: string; busy: boolean; error: unknown; onSave: (tenant: string) => void }) {
  const [value, setValue] = useState(current)
  const fieldError = error instanceof ApiError ? error.fields.microsoftTenantId : undefined
  return (
    <form className="toolbar" onSubmit={(e) => { e.preventDefault(); onSave(value) }}>
      <label>Your Microsoft tenant ID (optional)<input value={value} onChange={(e) => setValue(e.target.value)} placeholder="Leave empty to allow any organisation" style={{ width: 360 }} aria-invalid={fieldError ? true : undefined} /></label>
      <button type="submit" disabled={busy || value.trim() === current}>{value.trim() === '' && current !== '' ? 'Clear tenant' : 'Save tenant'}</button>
      {fieldError && <Notice tone="error">{fieldError}</Notice>}
    </form>
  )
}
